import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit'
import Container from 'typedi'
import i18n from 'i18n'

import { ApplicationState } from '@/state/root.models'
import { RestApiService } from '@/services'
import {
  setSnackbarAction,
  setLoadingFullAction,
} from '@/state/ui/global/slices/index.slices'

import ActionTypes from '../constants'
import {
  GetPlayerTransactionsPayload,
  PlayerTransactionsResponse,
  PlayerTransactionState,
} from '../models'

import { PLAYER_TRANSACTIONS } from './api.routes'
import { PlayerTransactionsActions } from '../slices/index.slices'

const restApiService: RestApiService = Container.get(RestApiService)

export const getAllPlayerTransactionsThunk = createAsyncThunk(
  ActionTypes.GET_ALL_PLAYER_TRANSACTIONS,
  async (payload: GetPlayerTransactionsPayload, thunkAPI) => {
    try {
      const { auth, playerCard } = thunkAPI.getState() as ApplicationState
      const { xlsx, ...queries } = payload

      if (!xlsx) {
        thunkAPI.dispatch(PlayerTransactionsActions.setIsloadingAction(true))
      }

      thunkAPI.dispatch(setLoadingFullAction(true))

      const body = {
        perPage: queries.limit || 10,
        page: queries.page || 1,
        tenantId: auth.brand.siteId.toString(),
        playerId: queries.playerId || playerCard.playerCard?.id,
        filters: {
          externalTransactionId: queries.externalTransactionId,
          transactionStatus: queries.transactionStatus
            ? [queries.transactionStatus]
            : undefined,
          transactionType: queries.transactionType,
          transactionSubType: queries.transactionSubType,
          submittedDate: queries.submittedDate,
          completedDate: queries.completedDate,
          walletType: queries.walletType,
          provider: queries.provider,
          group: queries.group,
          subGroup: queries.subGroup,
        },
      }

      restApiService.requestParams = {
        method: 'POST',
        endpoint: PLAYER_TRANSACTIONS.FIND_ALL,
        body,
        externalServerURL:
          process.env && process.env.REACT_APP_SERVER_INCENTIVE_API,
      }

      const response: PlayerTransactionsResponse =
        await restApiService.sendData(auth.brand.siteId.toString(), auth.token)

      thunkAPI.dispatch(setLoadingFullAction(false))

      if (response.status && response.status !== 200) {
        const message =
          typeof response.message === 'string'
            ? response.message
            : i18n.t('messages.errorOccurred')

        thunkAPI.dispatch(
          setSnackbarAction({
            message,
            open: true,
            variant: 'error',
          }),
        )
      }

      return response
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error,
        status: error,
      })
    }
  },
)

const getAllPlayerTransactionsExtraReducer = (
  builder: ActionReducerMapBuilder<PlayerTransactionState>,
) => {
  builder.addCase(
    getAllPlayerTransactionsThunk.fulfilled,
    (state, { payload, meta }) => {
      if (meta.arg.xlsx) return

      state.isLoading = false
      state.transactions = payload.items || []
      state.pagination = {
        ...state.pagination,
        total: payload.totalCount,
        pages: Math.ceil(payload.totalCount / (state.filters.limit || 25)),
        page: state.filters.page || 1,
        limit: state.filters.limit || 25,
      }
    },
  )
  builder.addCase(getAllPlayerTransactionsThunk.rejected, (state) => {
    state.isLoading = false
  })
}

export default getAllPlayerTransactionsExtraReducer
